// src/pages/notification/Notificationplace.js
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { format } from "date-fns";
import toast from "react-hot-toast";

import Spinner from "../../components/Spinner/Spinner";
import { getFutureMeetings } from "../../features/meeting/meetingSlice";
import { getDetailPath, pickMeetingId } from "../../utils/meetingUtils";

const TICK_MS = 30 * 1000;
const startOfTodayMs = () => { const d = new Date(); d.setHours(0,0,0,0); return +d; };
const endOfTodayMs   = () => { const d = new Date(); d.setHours(23,59,59,999); return +d; };
const parseStartMs = (v) => {
  if (!v) return NaN;
  if (typeof v === "number") return v;
  if (typeof v === "string") return new Date(v).getTime();
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  return NaN;
};

// Aynı toplantı için 5dk toast'ını sadece 1 kez göstermek üzere anahtar üret
const notifyKey = (id, startMs) => {
  const d = new Date(startMs);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `notified_5m_${id}_${y}-${m}-${dd}`;
};

export default function Notificationplace() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { meetings = [], isLoading = false } = useSelector((s) => s.meeting || {});

  // setTimeout kayıtları ve "tek sefer" kilidi
  const scheduledRef = useRef({}); // { [id]: timeoutId }
  const firedRef = useRef(new Set()); // gösterilmiş bildirimler: Set(key)

  const [, setTick] = useState(0);

  useEffect(() => { dispatch(getFutureMeetings()); }, [dispatch]);

  // 30 sn’de bir render: 30dk/5dk eşikleri canlı kalsın
  useEffect(() => {
    const t = setInterval(() => setTick((x) => x + 1), TICK_MS);
    return () => clearInterval(t);
  }, []);

  // 5 dk kala toast planla (sadece bugünküler) + TEK SEFER kilidi
  useEffect(() => {
    const now = Date.now();
    const sod = startOfTodayMs();
    const eod = endOfTodayMs();
    const newly = {};

    (meetings || []).forEach((m) => {
      const id = pickMeetingId(m);
      const startMs = parseStartMs(m?.startDate);
      if (!id || isNaN(startMs)) return;
      if (startMs < sod || startMs > eod) return; // sadece bugün

      const key = notifyKey(id, startMs);
      // localStorage veya ref’te zaten işaretlendiyse tekrar gösterme
      if (localStorage.getItem(key) === "1" || firedRef.current.has(key)) return;

      const notifyAt = startMs - 5 * 60 * 1000;
      const delay = notifyAt - now;

      const fire = () => {
        // tekrar kontrol (edge: timer geçene kadar başka render tetiklemiş olabilir)
        if (localStorage.getItem(key) === "1" || firedRef.current.has(key)) return;

        firedRef.current.add(key);
        try { localStorage.setItem(key, "1"); } catch {}

        toast.custom(
          (t) => (
            <div
              className="shadow-lg rounded-md p-4 bg-white text-gray-900"
              style={{ border: "1px solid rgba(0,0,0,0.08)", minWidth: 280 }}
            >
              <div style={{ fontWeight: 600, marginBottom: 6 }}>Toplantı başlamak üzere</div>
              <div style={{ fontSize: 14, marginBottom: 10 }}>
                {(m.title || "Toplantı")} {format(new Date(startMs), "HH:mm")}’da başlayacak. (5 dk kaldı)
              </div>
              <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                <button
                  onClick={() => {
                    const path = getDetailPath(m);
                    if (path) {
                      toast.dismiss(t.id);
                      navigate(path);
                    }
                  }}
                  style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e5e7eb", cursor: "pointer" }}
                >
                  Toplantıya git
                </button>
                <button
                  onClick={() => toast.dismiss(t.id)}
                  style={{ padding: "6px 10px", borderRadius: 8, border: "1px solid #e5e7eb", cursor: "pointer", background: "#f9fafb" }}
                >
                  Kapat
                </button>
              </div>
            </div>
          ),
          { position: "top-right", duration: 60000 }
        );
      };

      if (startMs > now) {
        if (delay > 0) {
          if (!scheduledRef.current[id]) newly[id] = setTimeout(fire, delay);
        } else if (startMs - now > -5 * 60 * 1000) {
          fire(); // 5 dk’dan az kalmışsa hemen
        }
      }
    });

    Object.assign(scheduledRef.current, newly);
    return () => Object.values(newly).forEach(clearTimeout);
  }, [meetings, navigate]);

  // sadece bugünkü ve başlamamış toplantılar
  const todayList = useMemo(() => {
    const now = Date.now(), sod = startOfTodayMs(), eod = endOfTodayMs();
    return (meetings || [])
      .map((m) => {
        const startMs = parseStartMs(m?.startDate);
        const etaMin = Math.floor((startMs - now) / 60000);
        return { ...m, _startMs: startMs, _etaMin: isNaN(etaMin) ? null : etaMin };
      })
      .filter((m) => m._startMs && m._startMs >= now && m._startMs >= sod && m._startMs <= eod)
      .sort((a, b) => a._startMs - b._startMs);
  }, [meetings]);

  if (isLoading) return <Spinner />;

  return (
    <div className="container mt-4">
      {/* <Toaster />  --> BUNU KALDIRDIK. Lütfen Toaster sadece App.js’te OLSUN. */}
      <h3 className="mb-4">Günlük Toplantılarım</h3>

      {!todayList.length ? (
        <p>Bugün için yaklaşan toplantı bulunmuyor.</p>
      ) : (
        <div className="list-group">
          {todayList.map((m) => {
            const timeStr = m._startMs ? format(new Date(m._startMs), "HH:mm") : "";
           const minsLeft =
  m._etaMin !== null && m._etaMin >= 0
    ? m._etaMin >= 60
      ? ` • ${Math.floor(m._etaMin / 60)} sa ${m._etaMin % 60} dk kaldı`
      : ` • ${m._etaMin} dk kaldı`
    : "";

            const showBell = m._etaMin !== null && m._etaMin <= 30; // 30 dk kala 🔔

            const go = (e) => {
              e?.preventDefault?.();
              const path = getDetailPath(m);
              if (path) navigate(path);
            };

            return (
              <div
                key={pickMeetingId(m)}
                className="list-group-item list-group-item-action text-left"
                role="button"
                tabIndex={0}
                onClick={go}
                onKeyDown={(e) => (e.key === "Enter" ? go(e) : null)}
                style={{ cursor: "pointer", display: "block" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 10, justifyContent: "space-between" }}>
                  <div style={{ minWidth: 0 }}>
                    <h5 className="mb-1" style={{ margin: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {m.title || "Toplantı"}
                    </h5>
                    {m.meetingDescription && (
                      <p className="mb-1" style={{ margin: "4px 0 0 0" }}>{m.meetingDescription}</p>
                    )}
                    <small>{timeStr}{minsLeft}</small>
                  </div>
                  {showBell && <span title="30 dk kaldı" aria-label="30 dk kaldı" style={{ fontSize: 18 }}>🔔</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
